import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useLocation } from 'react-router';
import { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { QUERY_CONFIG, API_ENDPOINTS, buildApiUrl } from '@/constants';
import { useSnackbar } from '@/context/SnackbarContext';
import { api } from '@/utils';
import { PageHeader } from '@/components/common';
import { CampaignQuestionsEditor } from '@/components/campaigns';

import type { Campaign } from '@/components/campaigns';

const CampaignPage: React.FC = () => {
    const { id: campaignId = null } = useParams<{ id: string }>();
    const location = useLocation();
    const backHref = (location.state as { returnUrl?: string })?.returnUrl || '/projects';
    const queryClient = useQueryClient();
    const { showSnackbar } = useSnackbar();
    const isRefreshingRef = useRef(false);

    // Fetch campaign details
    const {
        data: campaign,
        isError: campaignIsError,
        error: campaignError,
        isLoading: campaignIsLoading,
        isRefetching: campaignIsRefetching,
    } = useQuery({
        queryKey: ['api', 'campaigns', campaignId],
        queryFn: async () => {
            const url = buildApiUrl(API_ENDPOINTS.CAMPAIGN_BY_ID(Number(campaignId)));
            const response = await api.get(url, { timeout: QUERY_CONFIG.TIMEOUT });

            return response.data as Campaign;
        },
        staleTime: QUERY_CONFIG.STALE_TIME,
        refetchOnWindowFocus: QUERY_CONFIG.REFETCH_ON_WINDOW_FOCUS,
        retry: false,
        enabled: !!campaignId,
    });

    // Handle update of campaign and questions data when the page is refreshed
    const handleRefresh = () => {
        queryClient.invalidateQueries({ queryKey: ['api', 'campaigns', campaignId] });
        queryClient.invalidateQueries({ queryKey: ['api', 'campaign', Number(campaignId), 'questions'] });
        isRefreshingRef.current = true;
    };

    // Show snackbar when refresh completes
    useEffect(() => {
        if (isRefreshingRef.current && !campaignIsRefetching) {
            showSnackbar('Project data updated', 'success');
            isRefreshingRef.current = false;
        }
    }, [campaignIsRefetching, showSnackbar]);

    if (campaignIsLoading) {
        return (
            <Box mx={3} my={2}>
                <Typography>Loading project details...</Typography>
            </Box>
        );
    }

    if (campaignIsError) {
        return (
            <Box mx={3} my={2}>
                <Typography color="error">Error loading project: {(campaignError as Error).message}</Typography>
            </Box>
        );
    }

    if (!campaign) {
        return (
            <Box mx={3} my={2}>
                <Typography>Project not found</Typography>
            </Box>
        );
    }

    return (
        <Box>
            <PageHeader backLink={{ label: 'Back to projects', href: backHref }} onRefresh={handleRefresh}>
                <Typography variant="h2" component="h1">
                    {campaign.name}
                </Typography>
            </PageHeader>
            <Box p={2}>
                <CampaignQuestionsEditor campaignId={Number(campaignId)} />
            </Box>
        </Box>
    );
};

export default CampaignPage;
